import React, { useState } from 'react';
import { api, type Room } from '../lib/api';
import { OperaWindow, OperaSection } from './OperaWindow';
import { sortRoomsForTracker } from '../lib/bookings';

interface Props {
  rooms: Room[];
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Sets Room Tracker's room order.
 *
 * Move up / move down rather than a number per room. It is barely more code
 * than number inputs and removes the failure they invite — two rooms given the
 * same position, or a gap left after a deletion, either of which makes the
 * resulting order ambiguous. The list here is always a clean permutation, and
 * that is exactly what the endpoint stores.
 *
 * Room Plan is not affected by anything on this screen.
 */
export function RoomOrderModal({ rooms, onClose, onSaved }: Props) {
  const [order, setOrder] = useState<Room[]>(() => sortRoomsForTracker(rooms));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function move(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= order.length) return;
    const next = [...order];
    [next[index], next[target]] = [next[target], next[index]];
    setOrder(next);
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.setTrackerRoomOrder(order.map(r => r.id));
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  const unplaced = order.filter(r => r.trackerSortOrder == null).length;

  return (
    <OperaWindow
      title="ROOM TRACKER ORDER"
      onClose={onClose}
      size="compact"
      sideButtons={[
        { label: busy ? 'Saving…' : 'Save', onClick: save, primary: true, disabled: busy },
        { label: 'Cancel', onClick: onClose, disabled: busy },
      ]}
    >
      {error && <div className="modal-error">{error}</div>}

      <OperaSection title="Order on the board">
        <div className="ro-list">
          {order.map((r, i) => (
            <div key={r.id} className="ro-row">
              <span className="ro-pos">{i + 1}</span>
              <span className="ro-name">
                Room {r.number}
                {r.displayName && <span className="ro-sub">{r.displayName}</span>}
              </span>
              <span className="ro-moves">
                <button type="button" className="rt-btn" disabled={busy || i === 0}
                  onClick={() => move(i, -1)} aria-label={`Move room ${r.number} up`}>↑</button>
                <button type="button" className="rt-btn" disabled={busy || i === order.length - 1}
                  onClick={() => move(i, 1)} aria-label={`Move room ${r.number} down`}>↓</button>
              </span>
            </div>
          ))}
          {order.length === 0 && <div className="rt-empty">No rooms configured.</div>}
        </div>
      </OperaSection>

      <div style={{ fontSize: 11, color: '#555', marginTop: 8 }}>
        {unplaced > 0
          ? `${unplaced} room${unplaced === 1 ? '' : 's'} not yet placed — they sit at the end until you save this order.`
          : 'This order applies to Room Tracker only. Room Plan is unchanged.'}
      </div>
    </OperaWindow>
  );
}
